import { useContext } from 'react';
import useAppModal from 'hooks/useAppModal';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { CustomDataField } from 'models/CustomDataField';
import useCustomDataFieldsMutations from 'components/domain/customData/fields/useCustomDataFieldsMutations';
import { CUSTOM_DATA_FIELDS_BASE_QUERY_KEY } from 'components/domain/customData/fields/customDataFieldsQuery';
import useNotify from 'hooks/useNotify';
import { handlePaginationAfterDelete } from 'utils/pagination';
import TableContext from 'components/table/TableContext';

const useDeleteCustomFieldModal = (customField: CustomDataField) => {
  const queryClient = useQueryClient();
  const { tableInstance } = useContext(TableContext);
  const { notifyError } = useNotify();
  const { t } = useTranslation();
  const { openConfirmModal } = useAppModal();
  const { deleteCustomField } = useCustomDataFieldsMutations();

  const handleDelete = async () => {
    await deleteCustomField({ uid: customField.uid })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [CUSTOM_DATA_FIELDS_BASE_QUERY_KEY],
        });

        handlePaginationAfterDelete(tableInstance);
      })
      .catch(() => {
        notifyError(
          t('pageAutomations.customFields.errors.deleteCustomFieldError'),
        );
      });
  };

  const openDeleteConfirmationModal = () => {
    openConfirmModal({
      title: t('pageAutomations.customFields.deleteModal.title'),
      body: (
        <Trans
          i18nKey="pageAutomations.customFields.deleteModal.description"
          values={{
            name: customField.name,
          }}
          components={{
            br: <br />,
          }}
        />
      ),
      confirmColor: 'danger',
      confirmLabel: t('common.delete'),
      onConfirmAsync: handleDelete,
    });
  };

  return { openDeleteConfirmationModal };
};

export default useDeleteCustomFieldModal;
